import { useState, useRef } from "react";
import api, { apiError, downloadFile } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import CompliancePanel from "@/components/CompliancePanel";
import { toast } from "sonner";
import { ScanLine, MapPin, Camera, Search, Check, X, AlertTriangle, PenLine, Download } from "lucide-react";

const STEPS = ["GPS", "Recherche", "Photos", "Analyse", "Décision"];

export default function AgentScanner() {
  const [step, setStep] = useState(0);
  const [gps, setGps] = useState(null);
  const [radius, setRadius] = useState(50);
  const [nearby, setNearby] = useState([]);
  const [selected, setSelected] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [detected, setDetected] = useState({ text: "", color: "" });
  const [result, setResult] = useState(null);
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);

  const getGps = () => {
    if (!navigator.geolocation) return toast.error("GPS non supporté");
    navigator.geolocation.getCurrentPosition(
      (p) => { setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); toast.success("Position acquise"); },
      () => { setGps({ lat: 48.8566, lng: 2.3522, accuracy: 20, simulated: true }); toast.warning("GPS refusé — position de démo (Paris) utilisée"); }
    );
  };

  const search = async () => {
    if (!gps) return;
    setBusy(true);
    try { const { data } = await api.get(`/scan/nearby?lat=${gps.lat}&lng=${gps.lng}&radius=${radius}`); setNearby(data); if (!data.length) toast.info("Aucune enseigne approuvée à proximité. Élargissez le rayon."); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const onPhotos = (e) => {
    const files = Array.from(e.target.files).slice(0, 3);
    Promise.all(files.map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); }))).then((imgs) => setPhotos([...photos, ...imgs].slice(0, 3)));
  };

  const analyze = async (decision = null) => {
    setBusy(true);
    try {
      const { data } = await api.post("/inspections", { application_id: selected.id, gps, photos, detected, comments, decision, signature: "Agent" });
      setResult(data);
      if (decision) { toast.success(`Contrôle enregistré : ${decision}`); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const reset = () => { setStep(0); setGps(null); setNearby([]); setSelected(null); setPhotos([]); setResult(null); setComments(""); setDetected({ text: "", color: "" }); };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-2"><div className="w-10 h-10 bg-[#000091] flex items-center justify-center"><ScanLine size={22} className="text-white" /></div><div><div className="text-xs uppercase tracking-[0.2em] text-slate-500">Smart Sign Scanner</div><h1 className="font-display font-black text-2xl tracking-tight">Contrôle terrain</h1></div></div>

      <div className="flex gap-1 my-5">{STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 transition-colors ${i <= step ? "bg-[#000091]" : "bg-zinc-200"}`} />)}</div>

      <div className="card-flat p-6 min-h-[340px]">
        {step === 0 && (
          <div className="text-center py-8">
            <MapPin size={48} className="mx-auto text-[#000091] mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">Localisation GPS</h3>
            <p className="text-slate-500 mb-6">Récupérez votre position pour trouver les enseignes à contrôler à proximité.</p>
            <button onClick={getGps} data-testid="scan-gps-btn" className="bg-[#000091] text-white px-6 py-3 font-semibold hover:bg-blue-900 transition-colors">Acquérir la position</button>
            {gps && (
              <div className="mt-6 text-sm font-mono bg-zinc-50 border border-border p-3 inline-block">
                lat {gps.lat.toFixed(5)} · lng {gps.lng.toFixed(5)} · ±{Math.round(gps.accuracy)}m {gps.simulated && "(démo)"}
                <button onClick={() => setStep(1)} data-testid="scan-gps-next" className="ml-4 text-[#000091] font-bold">Continuer →</button>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div>
            <h3 className="font-display font-bold text-xl mb-4 flex items-center gap-2"><Search size={20} /> Enseignes à proximité</h3>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm">Rayon :</span>
              {[5, 10, 25, 50].map((r) => <button key={r} onClick={() => setRadius(r)} className={`px-3 py-1.5 text-sm border transition-colors ${radius === r ? "bg-[#000091] text-white border-[#000091]" : "border-border"}`}>{r}m</button>)}
              <button onClick={search} disabled={busy} data-testid="scan-search-btn" className="ml-auto bg-[#000091] text-white px-4 py-1.5 text-sm font-semibold">Rechercher</button>
            </div>
            <div className="space-y-2">
              {nearby.map((a) => (
                <button key={a.id} onClick={() => { setSelected(a); setDetected({ text: a.characteristics?.text || "", color: (a.characteristics?.colors || [])[0] || "" }); setStep(2); }} data-testid={`nearby-${a.reference}`}
                  className="w-full text-left card-flat p-4 hover:border-[#000091] transition-colors flex items-center justify-between">
                  <div><div className="font-semibold">{a.commercial?.business_name || a.merchant_name}</div><div className="text-sm text-slate-500 font-mono">{a.reference} · {a.distance_m}m</div></div>
                  <StatusBadge status={a.status} />
                </button>
              ))}
              {nearby.length === 0 && <p className="text-sm text-slate-500 text-center py-6">Lancez une recherche pour afficher les dossiers proches.</p>}
            </div>
          </div>
        )}

        {step === 2 && selected && (
          <div>
            <h3 className="font-display font-bold text-xl mb-1 flex items-center gap-2"><Camera size={20} /> Photos de l'enseigne</h3>
            <p className="text-sm text-slate-500 mb-4">Dossier : {selected.reference} — {selected.commercial?.business_name}</p>
            <label className="border-2 border-dashed border-border p-6 flex flex-col items-center cursor-pointer hover:border-[#000091] transition-colors mb-3">
              <Camera size={32} className="text-slate-400 mb-2" /><span className="text-sm font-medium">Prendre / ajouter des photos</span>
              <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onPhotos} data-testid="scan-photo-input" />
            </label>
            {photos.length > 0 && <div className="grid grid-cols-3 gap-2 mb-3">{photos.map((p, i) => <img key={i} src={p} alt="terrain" className="w-full h-20 object-cover border border-border" />)}</div>}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div><label className="text-sm font-medium block mb-1">Texte constaté (OCR)</label><input className="w-full border border-input px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-[#000091]" value={detected.text} onChange={(e) => setDetected({ ...detected, text: e.target.value })} /></div>
              <div><label className="text-sm font-medium block mb-1">Couleur constatée</label><input className="w-full border border-input px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-[#000091]" value={detected.color} onChange={(e) => setDetected({ ...detected, color: e.target.value })} /></div>
            </div>
            <button onClick={() => { setStep(3); analyze(); }} disabled={photos.length === 0} data-testid="scan-analyze-btn" className="w-full bg-[#000091] text-white py-3 font-semibold hover:bg-blue-900 transition-colors disabled:opacity-50">Analyser & comparer au projet validé</button>
          </div>
        )}

        {step === 3 && (
          <div>
            <h3 className="font-display font-bold text-xl mb-4">Analyse IA — comparaison</h3>
            {busy && <p className="text-slate-500">Analyse en cours...</p>}
            {result && (
              <div className="space-y-4">
                <div className="border border-border p-4 bg-zinc-50">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium">Similarité visuelle</span><span className="text-2xl font-black font-display text-[#000091]">{result.ai_analysis.visual_similarity}%</span></div>
                  <div className="text-sm text-slate-600">Confiance : <b>{result.ai_analysis.confidence}</b> · Méthode : {result.ai_analysis.method}</div>
                  <div className="text-xs text-amber-700 mt-2 flex items-center gap-1"><AlertTriangle size={13} /> {result.ai_analysis.estimated_dimensions?.note}</div>
                </div>
                {result.ai_analysis.differences?.length > 0 ? (
                  <div className="border border-amber-300 bg-amber-50 p-4"><div className="font-semibold text-amber-800 mb-1">Différences détectées</div>{result.ai_analysis.differences.map((d, i) => <div key={i} className="text-sm text-amber-800">⚠ {d.label}</div>)}</div>
                ) : <div className="border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">Aucune différence significative détectée.</div>}
                <CompliancePanel result={result.compliance_result} />
                <button onClick={() => setStep(4)} className="w-full bg-[#000091] text-white py-3 font-semibold hover:bg-blue-900 transition-colors">Prendre une décision →</button>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div>
            <h3 className="font-display font-bold text-xl mb-4">Décision de l'agent</h3>
            <p className="text-sm text-slate-500 mb-4">La décision finale reste humaine. Confirmez ou corrigez le résultat de l'analyse.</p>
            <div><label className="text-sm font-medium block mb-1">Commentaires</label><textarea className="w-full border border-input px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-[#000091] mb-4" rows={3} value={comments} onChange={(e) => setComments(e.target.value)} /></div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <button onClick={() => analyze("COMPLIANT")} disabled={busy} data-testid="decision-compliant" className="bg-emerald-600 text-white py-3 font-semibold hover:bg-emerald-700 transition-colors flex flex-col items-center gap-1"><Check size={18} /> Conforme</button>
              <button onClick={() => analyze("TO_VERIFY")} disabled={busy} data-testid="decision-verify" className="bg-amber-500 text-white py-3 font-semibold hover:bg-amber-600 transition-colors flex flex-col items-center gap-1"><AlertTriangle size={18} /> À vérifier</button>
              <button onClick={() => analyze("NON_COMPLIANT")} disabled={busy} data-testid="decision-noncompliant" className="bg-[#E1000F] text-white py-3 font-semibold hover:bg-red-700 transition-colors flex flex-col items-center gap-1"><X size={18} /> Non conforme</button>
            </div>
            <div className="border border-border p-3 flex items-center gap-2 text-sm text-slate-500 mb-4"><PenLine size={16} /> Signé électroniquement par l'agent</div>
            {result?.id && (
              <button onClick={() => downloadFile(`/inspections/${result.id}/report`, `rapport-${selected?.reference || "controle"}.pdf`)} data-testid="scan-download-report" className="w-full border border-[#000091] text-[#000091] py-2.5 font-semibold hover:bg-[#000091] hover:text-white transition-colors flex items-center justify-center gap-2 mb-3"><Download size={16} /> Télécharger le rapport PDF</button>
            )}
            <button onClick={reset} data-testid="scan-reset" className="w-full border border-border py-2.5 font-semibold hover:border-[#000091] transition-colors">Nouveau contrôle</button>
          </div>
        )}
      </div>
    </div>
  );
}
